import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useAuthStore } from '@/shared/auth/authStore';

import { useSidebarStore } from '@/shared/components/layout/_useSidebarStore';
import { useRetryableQuery } from '@/shared/apiClient/useRetryableQuery';
import { isMyLessonsRole, layoutMenuApi, type MyLessonRowDto } from '@/shared/components/layout/_api/layoutMenuApi';

import { BOTTOM_NAV_BY_ROLE, EMPTY_MENU, MOBILE_MENU_BY_ROLE, PERMISSION_MENU } from '@/shared/components/layout/_constants';
import { findAncestorExpandKeys } from '@/shared/components/layout/_activePath';
import type { SidebarMenuItem } from '@/shared/components/layout/types';

/** lazy 메뉴가 아직 목록을 못 채웠을 때 자리에 대신 넣는 안내 행의 id (클릭 불가, path 없음) */
const PLACEHOLDER_ID = {
  loading: '__loading__',
  error: '__error__',
  empty: '__empty__',
} as const;

type PlaceholderId = (typeof PLACEHOLDER_ID)[keyof typeof PLACEHOLDER_ID];

const placeholderChildren = (id: PlaceholderId, title: string) => [{ id, Icon: null, title, path: null }] satisfies NonNullable<SidebarMenuItem['children']>;

/** lazy 메뉴 하나의 children을 쿼리 상태에 따라 만들어 준다 */
function buildLessonChildren(
  item: SidebarMenuItem,
  lesson: { rows: MyLessonRowDto[] | undefined; isLoading: boolean; isError: boolean; isSuccess: boolean }
): SidebarMenuItem[] {
  if (lesson.isLoading) return placeholderChildren(PLACEHOLDER_ID.loading, '불러오는 중…');
  if (lesson.isError) return placeholderChildren(PLACEHOLDER_ID.error, '목록을 불러오지 못했습니다.');
  if (lesson.isSuccess && !lesson.rows?.length) return placeholderChildren(PLACEHOLDER_ID.empty, '등록된 수업이 없습니다.');
  // 위 세 경우에 해당하지 않는데 데이터도 없는 상태(쿼리 idle 등) — 표시할 것이 없으므로 빈 목록
  if (!lesson.rows?.length) return [];

  // 링크는 하나만 가질 수 있어 첫 번째 prefix 아래로 만들고, 활성 판정은 모든 prefix 아래 경로로 한다
  const basePaths = (item.activePathPrefix ?? []).map((prefix) => prefix.replace(/\/$/, ''));

  return lesson.rows
    ?.filter((row) => !row.ended)
    .map((row) => ({
      id: String(row.lessonId),
      Icon: null,
      title: row.lessonName,
      path: basePaths.length ? `${basePaths[0]}/${row.lessonId}` : null,
      activePathPrefix: basePaths.map((basePath) => `${basePath}/${row.lessonId}`),
    }));
}

/** 메뉴 트리(데스크톱 LNB든 모바일 드로어든) 한 벌에 펼쳐진 lazy 항목의 children을 채워 넣는다 */
function fillLazyChildren(
  menu: SidebarMenuItem[],
  expandedMenuKeys: string[],
  lesson: { rows: MyLessonRowDto[] | undefined; isLoading: boolean; isError: boolean; isSuccess: boolean }
): SidebarMenuItem[] {
  return menu.map((item) => {
    if (!item.lazyExpandChildren || !expandedMenuKeys.includes(item.id)) return item;
    if (item.lazyResourceKey !== 'expandedLesson') return { ...item, children: [] };
    return { ...item, children: buildLessonChildren(item, lesson) };
  });
}

/** MainLayout에서 사용할 로직: 경로·권한에 따라 사이드바 메뉴 등 결정 */
export function useMainLayoutLogic() {
  const userInfo = useAuthStore((state) => state.userInfo);
  const location = useLocation();

  const expandedMenuKeys = useSidebarStore((s) => s.expandedMenuKeys);
  const expandMenuKey = useSidebarStore((s) => s.expandMenuKey);

  const menu = PERMISSION_MENU[userInfo.userRole] ?? EMPTY_MENU;
  // 모바일 앱 셸의 드로어 전용 메뉴 — 데스크톱 LNB(menu)와 구성이 달라 따로 관리한다
  const mobileMenu = MOBILE_MENU_BY_ROLE[userInfo.userRole] ?? EMPTY_MENU;

  // 현재 경로를 소유한 항목이 화면에 보이도록 조상 메뉴를 자동으로 펼친다.
  // 깊이 제한 없이 훑는 이유: 딥링크·새로고침으로 2단 이상 중첩된 경로에 바로 들어오면
  // 조상이 접혀 있어 활성 표시가 아예 보이지 않는다.
  // toggle이 아닌 expand(멱등)를 쓰는 이유: StrictMode는 마운트 시 effect를 두 번 실행하는데,
  // toggle이면 두 번째 호출이 방금 연 메뉴를 다시 닫아버린다.
  useEffect(() => {
    findAncestorExpandKeys(menu, location.pathname)?.forEach(expandMenuKey);
    findAncestorExpandKeys(mobileMenu, location.pathname)?.forEach(expandMenuKey);
  }, [menu, mobileMenu, location.pathname, expandMenuKey]); // 라우트 이동·role 변경(로그인/전환) 모두에서 재평가

  // 동시에 여러 lazy 메뉴가 펼쳐질 수 있으므로, 'expandedLesson' 리소스를 쓰는 항목이 하나라도(데스크톱·모바일 통틀어) 열려 있으면 조회
  const isLessonMenuOpen = [...menu, ...mobileMenu].some(
    (m) => m.lazyExpandChildren && m.lazyResourceKey === 'expandedLesson' && expandedMenuKeys.includes(m.id)
  );

  // 원장·직원·로그아웃 상태에는 my-lessons 엔드포인트가 없다.
  // null로 좁혀 두면 enabled에서 호출을 막는 동시에, queryFn에 넘길 타입도 확정된다.
  const lessonsRole = isMyLessonsRole(userInfo.userRole) ? userInfo.userRole : null;

  const {
    data: lessonRows,
    isLoading: isLessonLoading,
    isError: isLessonError,
    isSuccess: isLessonSuccess,
  } = useRetryableQuery<MyLessonRowDto[]>({
    queryKey: ['layout', 'expandedLesson', userInfo.userRole],
    queryFn: () => {
      // enabled로 이미 막혀 있어 실제로는 도달하지 않는다. 타입 좁히기 겸 방어.
      if (!lessonsRole) throw new Error(`Unexpected role for my-lessons: ${userInfo.userRole}`);
      return layoutMenuApi.getMyLessons(lessonsRole);
    },
    enabled: isLessonMenuOpen && lessonsRole !== null,
    staleTime: 5 * 60 * 1000,
    withLoadingOverlay: false,
    retryMessage: '수업 목록을 불러오지 못했습니다.\n문제가 지속되면 관리자에게 문의해 주세요.\n\n다시 시도하시겠습니까?',
  });

  const lessonState = { rows: lessonRows, isLoading: isLessonLoading, isError: isLessonError, isSuccess: isLessonSuccess };

  const sidebarMenu: SidebarMenuItem[] = useMemo(
    () => fillLazyChildren(menu, expandedMenuKeys, lessonState),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [menu, expandedMenuKeys, lessonRows, isLessonLoading, isLessonError, isLessonSuccess]
  );

  // 모바일 앱 셸의 드로어 메뉴 — 데스크톱과 같은 lazy 채움 로직을 재사용한다
  const mobileSidebarMenu: SidebarMenuItem[] = useMemo(
    () => fillLazyChildren(mobileMenu, expandedMenuKeys, lessonState),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mobileMenu, expandedMenuKeys, lessonRows, isLessonLoading, isLessonError, isLessonSuccess]
  );

  // 모바일(Capacitor) 하단 탭바용 — 데스크톱 셸은 사용하지 않는다
  const bottomNavItems = BOTTOM_NAV_BY_ROLE[userInfo.userRole] ?? [];

  return { sidebarMenu, mobileSidebarMenu, bottomNavItems };
}
